import clsx from 'clsx'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import styled from 'styled-components'

import IPhone14CroppedFrame from './IPhone14CroppedFrame'
import { IPhone14CroppedFrameSymbolDefinition } from './IPhone14CroppedFrameSymbolDefinition'

import { accountAccessShowLogin } from 'actions/UiActions'
import HeadData from 'components/Common/HeadData/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import TextButton from 'LuxKit/Button/TextButton'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { verticalChildSpacing } from 'components/utils/spacing'
import { useAppDispatch } from 'hooks/reduxHooks'
import useInterval from 'hooks/useInterval'
import useNavigateTo from 'hooks/useNavigateTo'
import usePrevious from 'hooks/usePrevious'
import { getImageUrl } from 'lib/image/imageUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import HeaderLogo from 'tripPlanner/components/HeaderLogo'
import TripModal from 'tripPlanner/components/TripModal'
import CreateTrip from 'tripPlanner/components/TripModal/Trips/CreateTrip/CreateTripTP'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

const CAROUSEL_IMAGE_IDS: Array<[
  backgroundImageId: string,
  mobileShotImageId: string,
]> = [
  ['tp-hero-birthday', 'tp-mobile-shot-birthday-trip'],
  ['tp-hero-honeymoon', 'tp-mobile-shot-honeymoon'],
  ['tp-hero-school-vacation', 'tp-mobile-shot-school-holidays'],
  ['tp-hero-work', 'tp-mobile-shot-work-trip'],
]

const MOBILE_FRAME_IMAGE_PROPS: Partial<
  ComponentProps<typeof ResponsiveImage>
> = {
  mobileAspectRatio: '5:7',
  mobileWidth: '410',
  fit: 'center',
}

const SECTIONS_MOBILE_FRAME_DEMOS: Array<{
  videoSrc: string
  posterSrc: string
}> = [
  {
    videoSrc:
      'https://player.vimeo.com/progressive_redirect/playback/766750979/rendition/720p/file.mp4?loc=external&signature=71ed500953aec195c3807d06115155f14c8b99ab1520f46300180be4fd63f5fe',
    posterSrc: getImageUrl(
      'tp-mobile-shot-plan-trip-maldives',
      MOBILE_FRAME_IMAGE_PROPS,
    ),
  },
  {
    videoSrc:
      'https://player.vimeo.com/progressive_redirect/playback/765975598/rendition/720p/file.mp4?loc=external&signature=cde538b40a58f0a2c186a7af1c45e3affa4fabc44e24edbb553cf18918fbdfeb',
    posterSrc: getImageUrl(
      'tp-mobile-shot-compare-hotels',
      MOBILE_FRAME_IMAGE_PROPS,
    ),
  },
  {
    videoSrc:
      'https://player.vimeo.com/progressive_redirect/playback/774456400/rendition/720p/file.mp4?loc=external&signature=138aae48171980ab3dd9a8fdeb528aaee79bf7df706fac04f04dd11f6c1ba94a',
    posterSrc: getImageUrl(
      'tp-mobile-shot-map-hawaii-trip',
      MOBILE_FRAME_IMAGE_PROPS,
    ),
  },
  {
    videoSrc:
      'https://player.vimeo.com/progressive_redirect/playback/773192661/rendition/720p/file.mp4?loc=external&signature=2c867e90825d29b1331e0245e5ecb7bbf5213e8c4933f1d4eb5ea74e0ddf3d1f',
    posterSrc: getImageUrl(
      'tp-mobile-recommendations-airport',
      MOBILE_FRAME_IMAGE_PROPS,
    ),
  },
]

// durations in milliseconds
const CAROUSEL_FADE_DURATION: number = 500
const CAROUSEL_SLIDE_DURATION: number = 3500

const PageWrapper = styled.div`
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  height: 100%;
`

const CrossFadingCarousel = styled.div`
  position: relative;
  display: grid;
  grid-template-areas: 'slide';
  grid-template-columns: auto;
  grid-template-rows: auto;

  @keyframes trip-planner-hero-carousel-fade-out {
    from {
      opacity: 1;
    }

    to {
      opacity: 0;
    }
  }

  /* Isn't fade-in just fade-out but in backwards direction? Yes
  Why a separate keyframes definition then? So that by changing the animation-name,
  the whole animation can be re-triggered. */
  @keyframes trip-planner-hero-carousel-fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  > .slide {
    grid-area: slide;
    animation-name: trip-planner-hero-carousel-fade-out;
    animation-duration: ${CAROUSEL_FADE_DURATION}ms;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;

    &.fade-in {
      animation-name: trip-planner-hero-carousel-fade-in;
    }
  }
`

const SectionContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const SectionPhoneDisplay = styled(IPhone14CroppedFrame)`
  width: ${rem(360)};

  ${mediaQueryUp.tablet} {
    width: ${rem(410)};
  }
`

const SectionLayout = styled(LayoutContainer)`
  display: flex;
  flex-direction: column-reverse;
  gap: ${rem(32)};
  align-items: center;
  padding-top: ${rem(64)};
  position: relative;
  z-index: 0;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    gap: ${rem(32)};
    padding-top: ${rem(72)};

    &.reversed {
      flex-direction: row-reverse;
    }

    > ${SectionContentContainer} {
      flex-grow: 1;
    }

    > ${SectionPhoneDisplay} {
      align-self: end;
      flex-shrink: 0;
    }
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(80)};
  }
`

const VideoSectionLayout = styled(LayoutContainer)`
  text-align: center;
  padding-top: ${rem(48)};

  ${verticalChildSpacing[20]}
`

const VideoContainer = styled.div`
  max-width: ${rem(684)};
  margin: auto;
`

const HeroSectionOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px) saturate(1.3);
`

const Section = styled(PaletteSheet)`
  position: relative;
  background-color: ${(props) => props.theme.palette.neutral.default.eight};

  > ${CrossFadingCarousel} {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`

const HeroSectionContentContainer = styled(SectionContentContainer)`
  align-items: center;
  text-align: center;

  ${mediaQueryUp.tablet} {
    align-items: start;
    text-align: initial;
  }
`

const HeroSectionLogo = styled(HeaderLogo)`
  width: ${rem(157)};

  ${mediaQueryUp.tablet} {
    width: ${rem(195)};
  }
`

const HeroHeading = styled(Heading)`
  max-width: ${rem(550)};
`

const CtaLayout = styled(LayoutContainer)`
  padding-bottom: ${rem(64)};
  padding-top: ${rem(64)};
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`

interface MappedStateProps {
  isLoggedIn?: boolean
}

function TripPlannerAboutPage(props: MappedStateProps) {
  const { isLoggedIn } = props

  const [comittedAction, setComittedAction] = useState<
    'create new trip' | 'view trips' | null
  >(null)

  const hasLoggedIn = usePrevious(isLoggedIn)

  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0)

  const proceedToNextSlide = useCallback(() => {
    setCurrentSlideIndex(
      currentSlideIndex === CAROUSEL_IMAGE_IDS.length - 1 ?
        0 :
        currentSlideIndex + 1,
    )
  }, [currentSlideIndex])

  useInterval(() => {
    proceedToNextSlide()
  }, CAROUSEL_SLIDE_DURATION)

  const dispatch = useAppDispatch()
  const openModal = useOpenModal()
  const navigateTo = useNavigateTo()
  const intl = useIntl()

  const createNewTrip = useCallback(() => {
    if (isLoggedIn) {
      openModal(CreateTrip, { startDate: undefined, endDate: undefined })
    } else {
      setComittedAction('create new trip')
      dispatch(accountAccessShowLogin('tripPlannerLogin'))
    }
  }, [isLoggedIn, openModal, setComittedAction, dispatch])

  const viewTrips = useCallback(() => {
    if (isLoggedIn) {
      navigateTo('/trip-planner')
    } else {
      setComittedAction('view trips')
      dispatch(accountAccessShowLogin('tripPlannerLogin'))
    }
  }, [isLoggedIn, navigateTo, setComittedAction, dispatch])

  useEffect(() => {
    if (!hasLoggedIn && isLoggedIn) {
      switch (comittedAction) {
        case 'create new trip':
          openModal(CreateTrip, { startDate: undefined, endDate: undefined })
          break
        case 'view trips':
          navigateTo('/trip-planner')
          break
      }
    } else {
      setComittedAction(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  const tripPlannerTitle = intl.formatMessage({
    id: '1YT+DClTOY',
    defaultMessage: 'Trip Planner',
    description: 'Trip Planner product name used as the about page heading',
  })

  const heroSubtitle = intl.formatMessage({
    id: 'HAxxG5Ek3X',
    defaultMessage: 'Easily plan your perfect trip, all in one place',
    description: 'Subtitle describing Trip Planner on the about page hero section',
  })

  const createNewTripLabel = intl.formatMessage({
    id: '8QR0gTQpUo',
    defaultMessage: 'Create new trip',
    description: 'Button text to start creating a new trip in Trip Planner',
  })

  const viewMyTripsLabel = intl.formatMessage({
    id: 'SBZoIDeGCG',
    defaultMessage: 'View my trips',
    description: 'Button text to navigate to user trips list in Trip Planner',
  })

  return (
    <PageWrapper>
      <HeadData
        title={tripPlannerTitle}
        description={heroSubtitle}
      />
      <TripModal />
      <IPhone14CroppedFrameSymbolDefinition />

      <Section paletteType="inverse">
        <CrossFadingCarousel>
          {CAROUSEL_IMAGE_IDS.map(([backgroundImageId], index) => (
            <div
              key={`${backgroundImageId}-${index}`}
              className={clsx('slide', {
                'fade-in': currentSlideIndex === index,
              })}
            >
              <ResponsiveImage
                id={backgroundImageId}
                mobileAspectRatio="25:44"
                tabletAspectRatio="4:3"
                desktopAspectRatio="4:3"
                largeDesktopAspectRatio="5:3"
                mobileWidth="100vw"
                fit="center"
              />
            </div>
          ))}
        </CrossFadingCarousel>
        <HeroSectionOverlay />
        <SectionLayout className="reversed">
          <SectionPhoneDisplay>
            <CrossFadingCarousel>
              {CAROUSEL_IMAGE_IDS.map(([, mobileShotId], index) => (
                <div
                  key={`${mobileShotId}-${index}`}
                  className={clsx('slide', {
                    'fade-in': currentSlideIndex === index,
                  })}
                >
                  <ResponsiveImage
                    id={mobileShotId}
                    {...MOBILE_FRAME_IMAGE_PROPS}
                  />
                </div>
              ))}
            </CrossFadingCarousel>
          </SectionPhoneDisplay>
          <HeroSectionContentContainer>
            <HeroSectionLogo />
            <HeroHeading variant="heading1">
              {heroSubtitle}
            </HeroHeading>
            <Group
              direction="horizontal"
              horizontalAlign="center"
              tabletHorizontalAlign="start"
              gap={8}
              wrap="wrap"
            >
              <TextButton kind="primary" size="large" onClick={createNewTrip}>
                {createNewTripLabel}
              </TextButton>
              <TextButton kind="secondary" size="large" onClick={viewTrips}>
                {viewMyTripsLabel}
              </TextButton>
            </Group>
          </HeroSectionContentContainer>
        </SectionLayout>
      </Section>

      <Section paletteType="default">
        <VideoSectionLayout>
          <VideoContainer>
            <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
              <iframe
                src="https://player.vimeo.com/video/796121371?h=06a3315ca1&badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
                title={intl.formatMessage({
                  id: 'IOjeho3zXL',
                  defaultMessage: 'Trip Planner product launch video',
                  description: 'Title for Trip Planner promotional video iframe on about page',
                })}
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </VideoContainer>
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'Id9p88mVXv',
              defaultMessage: 'Discover the power of TripPlanner with this quick video demonstration.',
              description: 'Heading introducing the Trip Planner video demonstration on the about page',
            })}
          </Heading>
        </VideoSectionLayout>
      </Section>

      <Section paletteType="default">
        <SectionLayout>
          <SectionPhoneDisplay>
            <video
              controls={false}
              autoPlay
              playsInline
              loop
              muted
              poster={SECTIONS_MOBILE_FRAME_DEMOS[0].posterSrc}
            >
              <source
                src={SECTIONS_MOBILE_FRAME_DEMOS[0].videoSrc}
                type="video/mp4"
              />
            </video>
          </SectionPhoneDisplay>
          <SectionContentContainer>
            <Heading variant="heading2">
              {intl.formatMessage({
                id: 'xGAcM/hNz+',
                defaultMessage: 'Customise your trip effortlessly',
                description: 'Feature section heading about trip customisation on the about page',
              })}
            </Heading>
            <BodyText variant="large">
              {intl.formatMessage({
                id: 'HAZxqcji3q',
                defaultMessage: 'No matter where or who you booked with, you can add it all to your trip – hours of organising collated into one place!',
                description: 'Feature section body text explaining trip customisation capabilities',
              })}
            </BodyText>
          </SectionContentContainer>
        </SectionLayout>
      </Section>

      <Section paletteType="inverse">
        <SectionLayout className="reversed">
          <SectionPhoneDisplay>
            <video
              controls={false}
              autoPlay
              playsInline
              loop
              muted
              poster={SECTIONS_MOBILE_FRAME_DEMOS[1].posterSrc}
            >
              <source
                src={SECTIONS_MOBILE_FRAME_DEMOS[1].videoSrc}
                type="video/mp4"
              />
            </video>
          </SectionPhoneDisplay>
          <SectionContentContainer>
            <Heading variant="heading2" colour="neutral-one">
              {intl.formatMessage({
                id: 'VQlQNEIWji',
                defaultMessage: 'Save, compare and book great offers',
                description: 'Feature section heading about saving and comparing travel offers',
              })}
            </Heading>
            <BodyText variant="large" colour="neutral-one">
              {intl.formatMessage({
                id: 'rLrvzqk9xp',
                defaultMessage: 'Favourite and compare the world’s best hotels, experiences, flights, tours and cruises. Then when you’re ready, book it instantly.',
                description: 'Feature section body text about comparing and booking travel offers',
              })}
            </BodyText>
          </SectionContentContainer>
        </SectionLayout>
      </Section>

      <Section paletteType="default">
        <SectionLayout>
          <SectionPhoneDisplay>
            <video
              controls={false}
              autoPlay
              playsInline
              loop
              muted
              poster={SECTIONS_MOBILE_FRAME_DEMOS[2].posterSrc}
            >
              <source
                src={SECTIONS_MOBILE_FRAME_DEMOS[2].videoSrc}
                type="video/mp4"
              />
            </video>
          </SectionPhoneDisplay>
          <SectionContentContainer>
            <Heading variant="heading2">
              {intl.formatMessage({
                id: '8HDKknccF3',
                defaultMessage: 'Map it out and visualise your day-to-day itinerary',
                description: 'Feature section heading about mapping and visualising trip itinerary',
              })}
            </Heading>
            <BodyText variant="large">
              {intl.formatMessage({
                id: 'cPirA8B3LS',
                defaultMessage: 'From organising your daily itinerary to mapping out big ideas, you can see your Trip in one easy, interactive view.',
                description: 'Feature section body text about interactive itinerary map view',
              })}
            </BodyText>
          </SectionContentContainer>
        </SectionLayout>
      </Section>

      <Section paletteType="inverse">
        <SectionLayout className="reversed">
          <SectionPhoneDisplay>
            <video
              controls={false}
              autoPlay
              playsInline
              loop
              muted
              poster={SECTIONS_MOBILE_FRAME_DEMOS[3].posterSrc}
            >
              <source
                src={SECTIONS_MOBILE_FRAME_DEMOS[3].videoSrc}
                type="video/mp4"
              />
            </video>
          </SectionPhoneDisplay>
          <SectionContentContainer>
            <Heading variant="heading2" colour="neutral-one">
              {intl.formatMessage({
                id: 'VYmOu9bA1C',
                defaultMessage: 'Get tailored recommendations',
                description: 'Feature section heading about personalised trip recommendations',
              })}
            </Heading>
            <BodyText variant="large" colour="neutral-one">
              {intl.formatMessage({
                id: 'J1JZJ/p5OG',
                defaultMessage: 'Discover a better way to arrive, explore or experience your destination with our smart, personalised recommendations for flights, hotels, transfers, activities, tours and more, all based on gaps in your itinerary.',
                description: 'Feature section body text about smart personalised travel recommendations',
              })}
            </BodyText>
          </SectionContentContainer>
        </SectionLayout>
      </Section>

      <Section paletteType="default">
        <CtaLayout>
          <Heading variant="heading2" align="center">
            {intl.formatMessage({
              id: 'E2whaU74hV',
              defaultMessage: 'Let’s get planning!',
              description: 'Call-to-action heading encouraging users to start planning their trip',
            })}
          </Heading>
          <Group direction="horizontal" gap={8} horizontalAlign="center">
            <TextButton kind="primary" size="large" onClick={createNewTrip}>
              {createNewTripLabel}
            </TextButton>
            <TextButton kind="secondary" size="large" onClick={viewTrips}>
              {viewMyTripsLabel}
            </TextButton>
          </Group>
        </CtaLayout>
      </Section>
    </PageWrapper>
  )
}

export default connect<
  MappedStateProps,
  undefined,
  RouteComponentProps<any>,
  App.State
>((state) => ({
  isLoggedIn: selectLoggedIn(state),
}))(TripPlannerAboutPage)
